import React from 'react';
import type { PcoEvent, PcoSettings } from './types';

const CalendarView: React.FC<{ items: PcoEvent[] }> = ({ items }) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const monthName = now.toLocaleString('default', { month: 'long' });
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = now.getDate();

  const eventsByDay: Record<number, string[]> = {};
  items.forEach((e) => {
    if (!e.starts_at) return;
    const d = new Date(e.starts_at);
    if (d.getMonth() === month && d.getFullYear() === year) {
      const day = d.getDate();
      eventsByDay[day] = eventsByDay[day] || [];
      eventsByDay[day].push(e.name);
    }
  });

  const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const emptyCells = Array.from({ length: firstDay }, (_, i) => (
    <div className="pco-cal-day" key={`empty-${i}`} style={{ opacity: 0.3 }} />
  ));
  const dayCells = Array.from({ length: daysInMonth }, (_, i) => {
    const d = i + 1;
    const evts = (eventsByDay[d] || []).slice(0, 2);
    return (
      <div className={`pco-cal-day${d === today ? ' pco-today' : ''}`} key={d}>
        <div className="pco-cal-num">{d}</div>
        {evts.map((name, j) => <div className="pco-cal-event" key={j}>{name}</div>)}
      </div>
    );
  });

  return (
    <div className="pco-calendar">
      <div className="pco-cal-header"><h3>{monthName} {year}</h3></div>
      <div className="pco-cal-grid">
        {dayHeaders.map((d) => <div className="pco-cal-day-header" key={d}>{d}</div>)}
        {emptyCells}
        {dayCells}
      </div>
    </div>
  );
};

interface EventsProps {
  data: PcoEvent[];
  settings?: PcoSettings;
  onItemClick?: ((id: string) => void) | null;
}

const Events: React.FC<EventsProps> = ({ data, settings = {}, onItemClick }) => {
  if (!data || data.length === 0) {
    return <div className="pco-empty"><p>No upcoming events at this time.</p></div>;
  }

  const layout = settings['pco-layout'] || 'cards';
  const count = settings['pco-count'] || 'all';
  const limit = count === 'all' ? data.length : parseInt(count, 10);
  const visible = data.slice(0, limit);

  if (!visible.length) {
    return <div className="pco-empty"><p>No upcoming events.</p></div>;
  }

  if (layout === 'calendar') {
    return <CalendarView items={data} />;
  }

  if (layout === 'list') {
    return (
      <div className="pco-events-list">
        {visible.map((event) => (
          <div className="pco-event-list-item" key={event.id}>
            <div className="pco-list-date">
              {event.month && (
                <>
                  <div className="pco-month">{event.month}</div>
                  <div className="pco-day">{event.day}</div>
                </>
              )}
            </div>
            <div className="pco-list-details">
              <h3>{event.name}</h3>
              {event.description && <p>{event.description}</p>}
              {event.time && <span className="pco-time">🕐 {event.time}</span>}
              {event.location && <span className="pco-time"> · 📍 {event.location}</span>}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Cards (default)
  return (
    <div className="pco-events-grid">
      {visible.map((event) => (
        <div className="pco-card" key={event.id}>
          <div className="pco-card-image-wrap">
            {event.image_url ? (
              <img className="pco-card-image" src={event.image_url} alt={event.name} loading="lazy" />
            ) : (
              <div className="pco-card-image" />
            )}
            {event.month && event.day && (
              <div className="pco-date-badge">
                <div className="pco-month">{event.month}</div>
                <div className="pco-day">{event.day}</div>
              </div>
            )}
          </div>
          <div className="pco-card-body">
            <h3>{event.name}</h3>
            <div className="pco-meta">
              {event.time && <span>🕐 {event.time}</span>}
              {event.location && <span>📍 {event.location}</span>}
            </div>
            {event.description && <p>{event.description}</p>}
            {onItemClick ? (
              <button className="button" onClick={() => onItemClick(event.id)}>Details</button>
            ) : (
              <a className="button" href={event.url || '#'} data-turbolinks="false">Details</a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Events;
